/**
 * Client-side thumbnail generation for the asset library.
 *
 * The backend has no image processing, so thumbnails are produced in the browser
 * at upload time and posted alongside the original. Every thumbnail comes out at
 * the same dimensions via a centre cover-crop, which is what keeps the library
 * grid uniform no matter what shape the source image is.
 *
 * Thumbnails are decoration. Every failure path here returns null and the upload
 * proceeds without one — the grid falls back to the full image.
 */

/** 16:9, matching the fixed aspect box the library tiles render into. */
export const THUMBNAIL_WIDTH = 480;
export const THUMBNAIL_HEIGHT = 270;

type Decoded = {
    source: CanvasImageSource;
    width: number;
    height: number;
    release: () => void;
};

/**
 * createImageBitmap decodes off the main thread and works straight from a File,
 * with no object URL to leak. Browsers that lack it simply get no thumbnail.
 */
const decode = async (file: File): Promise<Decoded | null> => {
    if (typeof createImageBitmap !== 'function') return null;

    try {
        const bitmap = await createImageBitmap(file);
        return {
            source: bitmap,
            width: bitmap.width,
            height: bitmap.height,
            release: () => bitmap.close(),
        };
    } catch {
        return null;
    }
};

/**
 * The source rectangle to draw so the image fills the target box without
 * distortion: scale to whichever axis is tighter, then centre the overflow.
 *
 * Exported because this is the part that decides how a thumbnail is framed, and
 * it is worth pinning independently of the canvas work around it.
 */
export const coverCrop = (width: number, height: number) => {
    const scale = Math.max(THUMBNAIL_WIDTH / width, THUMBNAIL_HEIGHT / height);
    const cropWidth = Math.min(width, THUMBNAIL_WIDTH / scale);
    const cropHeight = Math.min(height, THUMBNAIL_HEIGHT / scale);

    return {
        sx: (width - cropWidth) / 2,
        sy: (height - cropHeight) / 2,
        sWidth: cropWidth,
        sHeight: cropHeight,
    };
};

const toBlob = (canvas: HTMLCanvasElement): Promise<Blob | null> =>
    new Promise(resolve => {
        // A browser that cannot encode the requested type silently produces
        // image/png instead, which is larger but perfectly usable — hence
        // deriving the filename extension from the blob rather than assuming.
        canvas.toBlob(resolve, 'image/webp', 0.8);
    });

/**
 * Returns a fixed-size thumbnail for `file`, or null if one could not be made.
 * Callers should treat null as normal and upload without a thumbnail.
 */
export const makeThumbnail = async (file: File): Promise<File | null> => {
    const decoded = await decode(file);
    if (!decoded) return null;

    try {
        const canvas = document.createElement('canvas');
        canvas.width = THUMBNAIL_WIDTH;
        canvas.height = THUMBNAIL_HEIGHT;

        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        ctx.imageSmoothingQuality = 'high';

        const { sx, sy, sWidth, sHeight } = coverCrop(decoded.width, decoded.height);
        ctx.drawImage(
            decoded.source,
            sx,
            sy,
            sWidth,
            sHeight,
            0,
            0,
            THUMBNAIL_WIDTH,
            THUMBNAIL_HEIGHT,
        );

        const blob = await toBlob(canvas);
        if (!blob) return null;

        const extension = blob.type === 'image/webp' ? 'webp' : 'png';

        return new File([blob], `thumb.${extension}`, { type: blob.type });
    } catch {
        // Canvas can throw on very large images (memory) — not worth failing the
        // upload over.
        return null;
    } finally {
        decoded.release();
    }
};
